import { action, observable } from 'mobx';
import {
  ADMIN_ADVISOR,
  ADMIN_ADVISOR_ACCOUNTS,
  ADMIN_ADVISOR_ACTIVATE,
  ADMIN_ADVISOR_IND,
  ADMIN_ADVISOR_TRANSFER,
  ADMIN_ADVISORS,
} from '../api/endpoints';
import { FormStore } from 'stores/formStore';
import { FormikHelpers } from 'formik';
import { AccountStatus, Currency } from 'enums';
import { Role, SingleValueResponse } from 'common/interfaces';
import { APP_CONFIG } from 'appConfig';
import { realms } from 'helpers/realms';

export interface AdvisorData {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  degreePrefix: string;
  degreeSuffix: string;
  companyNumber: string;
  code: string;
  address: {
    country: string;
  };
  options: {
    country: string[];
    role: string[];
  };
  customLogoEnabled: boolean;
  reactivationEnabled: boolean;
  individualPortfolioEnabled: boolean;
  currencyEur: boolean;
  representedCompany: string;
  representedCompanyNumber: string;
  username: string;
  realm: string;
  role: Role;
}

export const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  degreePrefix: '',
  degreeSuffix: '',
  companyNumber: '',
  code: '',
  address: { country: '' },
  representedCompany: '',
  representedCompanyNumber: '',
};

export interface Advisor {
  code: string;
  username: string;
  name: string;
  intermediaryCode: string;
  realm: string;
  activeSince: string;
  reactivationEnabled: boolean;
  individualPortfolioEnabled: boolean;
  role: Role;
}

export interface Account {
  accountId: string;
  variableSymbol: string;
  title: string;
  created: string;
  aum: number;
  clientName: string;
  status: AccountStatus;
  pendingSignature: boolean;
  currency: Currency;
  advisorKeycloakId: string;
}

const PAGE_SIZE_KEY = 'advisorsPageSize';

export class AdvisorsStore extends FormStore<AdvisorData> {
  @observable pageSize = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '20');
  @observable advisors: Advisor[] | null = null;
  @observable advisor?: AdvisorData | null = null;
  @observable loading = false;
  @observable generationDialog = false;
  @observable generationDate?: string = undefined;
  @observable generationDrive = false;
  @observable cacheDialog = false;

  @action hideGenerationDialog = () => (this.generationDialog = false);
  @action hideCacheDialog = () => (this.cacheDialog = false);

  @action
  fetchAdvisors = async () => {
    this.loading = true;
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<Advisor[]>(ADMIN_ADVISORS);
    if (response && response.data) {
      this.advisors = response.data.map((a) => {
        return { id: a.username, ...a };
      });
    }
    this.loading = false;
  };

  @action
  fetchAdvisor = async (realm: string, advisorName: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.fetchData(ADMIN_ADVISOR(realm, advisorName));
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  activateAdvisor = async (realm: string, advisorName: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.post<AdvisorData>(ADMIN_ADVISOR_ACTIVATE(realm, advisorName));
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  removeAdvisor = async (realm: string, advisorName: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.delete<AdvisorData>(ADMIN_ADVISOR(realm, advisorName));
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  setIndividualPortfolio = async (realm: string, advisorName: string, enabled: boolean): Promise<boolean> => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<boolean>>(
      ADMIN_ADVISOR_IND(realm, advisorName),
      {
        individualPortfolioEnabled: enabled,
      },
    );
    this.rootStore.stores.uiStore.setLoading(false);
    const ok = !!response?.data?.value;
    ok && this.updateLocalAdvisorData(realm, advisorName, enabled);
    return ok;
  };

  @action
  updateLocalAdvisorData = (
    realm: string,
    username: string,
    individualPortfolioEnabled: boolean,
    code?: string,
    role?: Role,
    firstname?: string,
    lastname?: string,
  ) => {
    const index = this.advisors?.findIndex((a) => a.realm === realm && a.username === username);
    if (index && index > -1) {
      this.advisors![index].individualPortfolioEnabled = individualPortfolioEnabled;
      if (code) this.advisors![index].code = code;
      if (role) this.advisors![index].role = role;
      if (firstname && lastname) this.advisors![index].name = `${firstname} ${lastname}`;
      this.advisors = this.advisors!.slice();
    }
  };

  handleSubmit = async (
    realm: string,
    advisorName: string,
    values: AdvisorData,
    formikHelpers: FormikHelpers<AdvisorData>,
  ) => {
    return await this.submitData(ADMIN_ADVISOR(realm, advisorName), values, {
      formikHelpers,
      reset: false,
      validationPrefixDepth: 0,
      singleValueResponse: true,
    });
  };

  @action fetchAccounts = async (realm: string, userName: string) => {
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<Account[]>(ADMIN_ADVISOR_ACCOUNTS(realm, userName));
    if (response && response.data) {
      return response.data;
    } else {
      return null;
    }
  };

  @action onChangeRowsPerPage = (pageSize: number) => {
    this.pageSize = pageSize;
    localStorage.setItem(PAGE_SIZE_KEY, JSON.stringify(pageSize));
  };

  @action transfer = async (
    realm: string,
    advisorName: string,
    targetRealm: string,
    targetUsername: string,
    accounts: string[],
  ): Promise<boolean> => {
    this.rootStore.stores.uiStore.setLoading(true);

    const response = await this.rootStore.stores.apiStore.post<{ value: boolean }>(
      ADMIN_ADVISOR_TRANSFER(realm, advisorName),
      {
        targetRealm,
        targetUsername,
        accounts,
      },
    );

    this.rootStore.stores.uiStore.setLoading(false);
    return response?.data?.value || false;
  };

  @action
  downloadAccounts = async () => {
    const { post } = this.rootStore.stores.apiStore;
    const { uiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    const response = await post<{ value: string }>(ADMIN_ADVISORS);
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDialog = true;
    }
    uiStore.setLoading(false);
  };

  get authToken(): string {
    return this.rootStore.stores.userStore.getToken();
  }

  impersonate = async (realm: string, keycloakId?: string) => {
    window.open(`${APP_CONFIG.kcAuth}/admin/master/console/#/${realm}/users/${keycloakId}/settings`);
  };

  goToAccount = async (realm: string, accountId: string, status: AccountStatus) => {
    const domain = Object.entries(realms).find(([key, value]) => value === realm);
    const path = [AccountStatus.DRAFT, AccountStatus.MOCK].includes(status)
      ? 'edit/basic'
      : [AccountStatus.ACTIVE, AccountStatus.AWAITING_APPROVAL].includes(status)
      ? 'detail'
      : 'documents';
    window.open(
      `${APP_CONFIG.protocol}://${domain ? domain[0] : ''}.${APP_CONFIG.domain}/accounts/${accountId}/${path}`,
    );
  };

}
